import React from 'react'
import { productApi } from 'api/product'
import {
  IEnhanceProductRequest,
  IEnhancementImageVariant,
  ILatestEnhancementDescription,
  TEnhanceProductImageListType,
  TEnhanceProductImageResult
} from 'models/product'
import {
  EnhanceSocketEvent,
  EnhanceSocketIncomingEvent
} from 'models/app'
import { API_URL } from 'shared/constants/urls'
import { io } from 'socket.io-client'
import { normalizeEnhancementImage } from '../utils/enhanceProduct'

type TEnhanceJobKey = 'description' | 'image'
type TEnhanceJobStatus = 'idle' | 'running' | 'completed' | 'error'

interface IEnhanceJobState {
  status: TEnhanceJobStatus
  percent: number
  message: string
  error: string
}

interface IEnhanceProductJobsResult {
  description: ILatestEnhancementDescription | null
  image: IEnhancementImageVariant | null
}

const initialJobState: IEnhanceJobState = {
  status: 'idle',
  percent: 0,
  message: '',
  error: ''
}

const getInitialJobStates = (): Record<TEnhanceJobKey, IEnhanceJobState> => ({
  description: { ...initialJobState },
  image: { ...initialJobState }
})

const getApiOrigin = () => API_URL.replace(/\/api\/?$/, '')

const enhanceSocket = io(getApiOrigin(), {
  autoConnect: false,
  transports: ['websocket', 'polling'],
  withCredentials: true
})

const getResponseList = (
  value: unknown
): IEnhancementImageVariant[] => {
  if (Array.isArray(value)) {
    return value as IEnhancementImageVariant[]
  }

  const maybeObject = value as {
    data?: IEnhancementImageVariant[]
    items?: IEnhancementImageVariant[]
  }

  return maybeObject?.data ?? maybeObject?.items ?? []
}

const getJobErrorMessage = (payload: unknown): string => {
  const data = payload as {
    error?: string
    message?: string
    reason?: string
  }

  if (data?.error) {
    return data.error
  }

  if (data?.message) {
    return data.message
  }

  if (data?.reason) {
    return data.reason
  }

  try {
    return JSON.stringify(payload)
  } catch {
    return 'Ошибка генерации'
  }
}

export const useEnhanceProductJobs = (productId: string) => {
  const [isRunning, setIsRunning] = React.useState(false)
  const [jobStates, setJobStates] = React.useState(getInitialJobStates)
  const [result, setResult] = React.useState<IEnhanceProductJobsResult>({
    description: null,
    image: null
  })

  const jobKeyByIdRef = React.useRef<Record<string, TEnhanceJobKey>>({})
  const completedJobsRef = React.useRef<Set<string>>(new Set())
  const imageListTypeRef = React.useRef<TEnhanceProductImageListType>('full')

  const reset = React.useCallback(() => {
    setIsRunning(false)
    setJobStates(getInitialJobStates())
    setResult({ description: null, image: null })
    jobKeyByIdRef.current = {}
    completedJobsRef.current = new Set()
  }, [])

  const ensureSocketConnected = React.useCallback(async () => {
    if (enhanceSocket.connected) {
      return
    }

    enhanceSocket.connect()

    await new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(new Error('WebSocket connection timeout'))
      }, 5000)

      const onConnect = () => {
        window.clearTimeout(timeoutId)
        enhanceSocket.off('connect', onConnect)
        enhanceSocket.off('connect_error', onError)
        resolve()
      }

      const onError = (error: unknown) => {
        window.clearTimeout(timeoutId)
        enhanceSocket.off('connect', onConnect)
        enhanceSocket.off('connect_error', onError)
        reject(error)
      }

      enhanceSocket.on('connect', onConnect)
      enhanceSocket.on('connect_error', onError)
    })
  }, [])

  const markJobCompleted = React.useCallback((jobId: string) => {
    completedJobsRef.current.add(jobId)
    const totalJobs = Object.keys(jobKeyByIdRef.current).length

    if (totalJobs && completedJobsRef.current.size >= totalJobs) {
      setIsRunning(false)
    }
  }, [])

  React.useEffect(() => {
    const handleStarted = (payload: { jobId: string; message?: string }) => {
      const key = jobKeyByIdRef.current[payload.jobId]
      if (!key) {
        return
      }

      setJobStates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          status: 'running',
          message: payload.message ?? '',
          error: ''
        }
      }))
    }

    const handleProgress = (payload: {
      jobId: string
      percent?: number
      message?: string
    }) => {
      const key = jobKeyByIdRef.current[payload.jobId]
      if (!key) {
        return
      }

      setJobStates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          status: 'running',
          percent: payload.percent ?? prev[key].percent,
          message: payload.message ?? prev[key].message
        }
      }))
    }

    const handleCompleted = async (payload: {
      jobId: string
      result: unknown
    }) => {
      const key = jobKeyByIdRef.current[payload.jobId]
      if (!key) {
        return
      }

      try {
        if (key === 'description') {
          const fallback = payload.result as ILatestEnhancementDescription
          const response = await productApi.getLatestAiDescription(productId)
          setResult((prev) => ({
            ...prev,
            description: response.data?.new_desc ? response.data : fallback
          }))
        } else {
          const fallback = normalizeEnhancementImage(
            payload.result as TEnhanceProductImageResult
          )
          const response = await productApi.getAiImages(
            productId,
            imageListTypeRef.current
          )
          const list = getResponseList(response.data)
          setResult((prev) => ({
            ...prev,
            image: list[list.length - 1] ?? fallback
          }))
        }
      } catch {
        if (key === 'description') {
          setResult((prev) => ({
            ...prev,
            description: payload.result as ILatestEnhancementDescription
          }))
        } else {
          setResult((prev) => ({
            ...prev,
            image: normalizeEnhancementImage(
              payload.result as TEnhanceProductImageResult
            )
          }))
        }
      }

      setJobStates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          status: 'completed',
          percent: 100,
          message: 'Готово'
        }
      }))
      markJobCompleted(payload.jobId)
    }

    const handleError = (payload: {
      jobId: string
      error?: string
      message?: string
      reason?: string
    }) => {
      const key = jobKeyByIdRef.current[payload.jobId]
      if (!key) {
        return
      }

      setJobStates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          status: 'error',
          error: getJobErrorMessage(payload)
        }
      }))
      markJobCompleted(payload.jobId)
    }

    enhanceSocket.on(EnhanceSocketEvent.PROCESSING_STARTED, handleStarted)
    enhanceSocket.on(EnhanceSocketEvent.PROCESSING_PROGRESS, handleProgress)
    enhanceSocket.on(EnhanceSocketEvent.PROCESSING_COMPLETED, handleCompleted)
    enhanceSocket.on(EnhanceSocketEvent.PROCESSING_ERROR, handleError)

    return () => {
      enhanceSocket.off(EnhanceSocketEvent.PROCESSING_STARTED, handleStarted)
      enhanceSocket.off(EnhanceSocketEvent.PROCESSING_PROGRESS, handleProgress)
      enhanceSocket.off(EnhanceSocketEvent.PROCESSING_COMPLETED, handleCompleted)
      enhanceSocket.off(EnhanceSocketEvent.PROCESSING_ERROR, handleError)
    }
  }, [markJobCompleted, productId])

  const start = React.useCallback(
    async (
      request: IEnhanceProductRequest,
      imageListType: TEnhanceProductImageListType
    ) => {
      reset()
      setIsRunning(true)
      imageListTypeRef.current = imageListType

      try {
        await ensureSocketConnected()
        const response = await productApi.enhanceProduct(request)
        const jobKeyById: Record<string, TEnhanceJobKey> = {}

        if (response.data.descriptionJobId) {
          jobKeyById[response.data.descriptionJobId] = 'description'
        }

        if (response.data.imageJobId) {
          jobKeyById[response.data.imageJobId] = 'image'
        }

        jobKeyByIdRef.current = jobKeyById

        setJobStates((prev) => ({
          ...prev,
          ...(response.data.descriptionJobId
            ? { description: { ...prev.description, status: 'running' as const } }
            : {}),
          ...(response.data.imageJobId
            ? { image: { ...prev.image, status: 'running' as const } }
            : {})
        }))

        Object.keys(jobKeyById).forEach((jobId) => {
          enhanceSocket.emit(EnhanceSocketIncomingEvent.SUBSCRIBE_TO_JOB, { jobId })
        })

        if (!Object.keys(jobKeyById).length) {
          setIsRunning(false)
        }
      } catch (e) {
        setIsRunning(false)
        setJobStates((prev) => ({
          ...prev,
          description: request.description
            ? { ...prev.description, status: 'error', error: 'Ошибка запуска' }
            : prev.description,
          image: request.image
            ? { ...prev.image, status: 'error', error: 'Ошибка запуска' }
            : prev.image
        }))
        throw e
      }
    },
    [ensureSocketConnected, reset]
  )

  return {
    isRunning,
    jobStates,
    result,
    start,
    reset
  }
}
